import './CustomSelectBox.less';

import clsx from 'clsx';
import ChevronIcon from '../../icons/ChevronDownIcon';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useOutsideClick } from '../turboLive/hooks/useOutsideClick';
import { NfSelect, selectOptions, makeField } from 'nike-form-js';
import { useReframe } from 'nike-re-framejs';
import FieldErrors from './FieldErrors';

function FormSelect({ path, options, mandatory, emptyLabel, disabled, selectedCode, field }) {
    const { deref } = useReframe();
    const value = deref(field.getValue());
    const emptyItem = mandatory ? [['', emptyLabel, true]] : [['', emptyLabel]];
    const modOptions = !value && emptyLabel ? emptyItem.concat(options) : options;

    const id = '' + path;
    useEffect(() => {
        field.setValue(selectedCode);
    }, [selectedCode, field]);
    return (
        <NfSelect
            field={field}
            attrs={{
                className: 'hide',
                id,
                disabled
            }}
        >
            {selectOptions(modOptions)}
        </NfSelect>
    );
}

interface CustomSelectBoxProps {
    children: ReactNode;
    formLikeView?: boolean;
    selectedItem: any;
    dropDownShort?: boolean;
    onClick?: () => void;
    isMobile?: boolean;
    disabled?: boolean;
    form?: any;
    selectedCode?: any;
    path?: string;
    options?: any;
    mandatory?: any;
    emptyLabel?: string;
    label?: string;
    hint?: string;
    style?: 'normal' | 'black';
}

/**
 *
 * @param children - used for dropDown options - it should always be array of DropDownOption - it can be simple text info, but it can also be some complicated stuff but
 * try not to overdo it
 * @param formLikeView - use for default visual of component
 * @param selectedItem - text information for "selection button"
 * @param dropDownShort - short version of dropDownMenu - use only for portal view
 * @param onClick - if you need something special, other than opening dropDown - use it for opening mobile dialogs with selections
 * @param isMobile - used on mobile ? let me know
 * @param disabled
 * @param form - if component is used in forms it should contain form
 * @param selectedCode - when using form - you need this for nike-form-js component
 * @param path - if used with form
 * @param options - when using form - you need to set options for NfSelect - it's always duo [code, text]
 * @param mandatory - still no ide what does it mean, but it was in old component
 * @param emptyLabel - it definitely has some use
 * @param label - mainly for "form" purposes - it is text information label about, what is selection content
 * @param hint - if you want to give user some type of hint about this input
 *
 */

function CustomSelectBox({
    children,
    formLikeView = false,
    selectedItem,
    dropDownShort,
    onClick,
    isMobile,
    disabled,
    form,
    selectedCode,
    path,
    options,
    mandatory,
    emptyLabel,
    label,
    hint,
    style = 'normal'
}: CustomSelectBoxProps) {
    const [active, setActive] = useState(false);
    const { deref } = useReframe();
    const dropDownRef = useRef(null);
    const field = form && makeField(form, path);
    const isTouched = field && deref(field.isTouched());
    const errors = field && deref(field.getErrors());
    const isError = isTouched && errors.size > 0;
    useOutsideClick(dropDownRef, () => setActive(false));
    function onSelectionClick(e) {
        e.preventDefault();
        if (!disabled) {
            if (onClick) {
                onClick();
            }
            setActive(!active);
        }
    }

    //toto je urcite absolutne zle riesenie, ale nenasiel som sposob ako to spravit abstraktne a zaroven k tomu neptorebovat nejake osobitne speci funkcie do kazdej
    //komponenty kde by sa to pouzilo :/
    useEffect(() => {
        setActive(false);
    }, [selectedItem]);

    return (
        <label
            data-path={path && path[0]}
            className={clsx('nui-input p-rel flex flex-col', {
                'nui-input-disabled': disabled,
                'mb-20': formLikeView
            })}
        >
            {label ? (
                <div className={clsx('nui-input-label fs-14 text-extra-bold', style === 'black' && 'nui-input-black')}>
                    {label}
                </div>
            ) : null}
            {hint ? <div className="nui-input-hint fs-12">{hint}</div> : null}
            <div
                className={clsx('flex br-radius-xs p-rel', {
                    'custom-select-box-40 fs-16 my-5 br-default': formLikeView,
                    'custom-select-box-30 fs-12 custom-select-box-border-light': !formLikeView,
                    'custom-select-box-disabled': formLikeView && disabled
                })}
                ref={dropDownRef}
            >
                <button
                    className={'full-width flex items-center justify-between px-10'}
                    onClick={onSelectionClick}
                    title={selectedItem}
                >
                    <span className="flex-1 text-left ellipsis" data-atid="selected-market-item">
                        {selectedItem}
                    </span>
                    <ChevronIcon
                        className={clsx({
                            'custom-select-box-icon': formLikeView || form,
                            'custom-select-box-icon-small c-white text-bold': !formLikeView && !form,
                            'arrow-up': active,
                            'c-orange': !disabled && formLikeView,
                            'c-black-20': disabled,
                            hide: disabled && !form && !formLikeView
                        })}
                        dataAtid="selected-market-arrow"
                    />
                </button>

                {form && (
                    <FormSelect
                        mandatory={mandatory}
                        disabled={disabled}
                        options={options}
                        emptyLabel={emptyLabel}
                        path={path}
                        selectedCode={selectedCode}
                        field={field}
                    />
                )}
                {active && !isMobile ? (
                    form ? (
                        <DropDown small={dropDownShort}>{children}</DropDown>
                    ) : (
                        <>{children}</>
                    )
                ) : null}
            </div>
            {/*@ts-ignore*/}
            {isError ? <FieldErrors errors={errors} /> : null}
        </label>
    );
}

//width magic is something you should never do, but I give you power to try, you know we live just once, so why not ;)
export function DropDown({
    children,
    small,
    dropDownRef,
    width,
    formView
}: {
    children: ReactNode;
    small?: boolean;
    dropDownRef?: any;
    width?: number;
    formView?: boolean;
}) {
    return (
        <div
            ref={dropDownRef}
            className={clsx('custom-select-box-drop-down flex flex-col p-abs bg-white c-black-100  mt-5 br-radius-xs', {
                'custom-select-box-drop-down-300': !small,
                'custom-select-box-drop-down-150': small,
                'full-width': !width,
                'fs-16': formView,
                'fs-12': !formView
            })}
            style={{ width: width }}
        >
            {children}
        </div>
    );
}

export function DropDownOption({ option, onSelect, className = 'py-8 px-20' }) {
    return (
        <button
            onClick={onSelect}
            className={clsx('custom-select-box-drop-down-item flex items-center text-left', className)}
        >
            {option}
        </button>
    );
}

export default CustomSelectBox;
